import React, { useEffect, useState, useRef, useCallback, Suspense, lazy } from "react";
import { init } from "@noriginmedia/norigin-spatial-navigation";
import { Menu } from "../layout/sidebar";
import SlideAnimationFrame from "../common/slideAnimationFrame";
import { useDispatch, useSelector } from "react-redux";
import {
  resetAuth,
  setActiveSeciton,
  setPhone,
} from "../../redux/Slices/auth.slice";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { trackGoogleAnalyticsEvent } from "../../services/googleAnalytics";
import {
  deleteSessionWithToken,
  generateLoginToken as generateLoginAttemptToken,
  getAuthErrorMessage,
  getSessionsWithToken,
  getUser,
  getUserWithToken,
  loginRegister,
  selectProfile,
  selectQRProfile,
  sendLoginOTP,
} from "../../services/authService";
import {
  AUTH_STORAGE_KEYS,
  clearLoginAttemptToken,
  getAccessToken,
  getLoginAttemptExpiry,
  getLoginAttemptToken,
  setLoginAttemptToken,
  setTemporaryAuthTokens,
} from "../../services/authStorage";
import {
  fetchPublicAuthConfig,
  getMaxProfileCount,
  getMaxSessionCount,
  getPublicAuthConfigSync,
  getServerNow,
  subscribePublicAuthConfig,
} from "../../services/publicAuthConfigService";
import NextPlayGuideHelper from "../../utils/nextPlayGuideHelper";
import { focusPhoneLoginDefaultOnce } from "./focusDefaults";

// Lazy load login step components for code splitting
var LoginPageContent = lazy(function () { return import("./loginPageStep1"); });
var LoginVerifyPageContent = lazy(function () { return import("./loginPageStep2"); });
var SelectProfileView = lazy(function () { return import("./loginPageStep3"); });
var SessionManagementModal = lazy(function () { return import("./SessionManagementModal"); });

// Initialize spatial navigation
init({
  debug: false,
  visualDebug: false,
  rtl: true,
});

// Fallback component for Suspense
function LoadingSpinner() {
  return (
    <div className="legacy-login-fallback">
      <div className="legacy-login-fallback-cell">
        <div className="legacy-login-spinner" style={{ width: 48, height: 48 }} />
      </div>
    </div>
  );
}

/**
 * LAT Storage helper
 */
var LoginAttemptStorage = {
  setToken: function (token, expiresAt) {
    if (token && typeof token === 'string') {
      setLoginAttemptToken(token, expiresAt);
    }
  },

  getToken: function () {
    return getLoginAttemptToken();
  },

  getTokenExpiry: function () {
    return getLoginAttemptExpiry();
  },

  isTokenExpired: function () {
    var expiry = this.getTokenExpiry();
    if (!expiry) return true;
    return getServerNow() > expiry;
  },

  isTokenValid: function () {
    var token = this.getToken();
    if (!token) return false;
    return !this.isTokenExpired();
  },

  clear: function () {
    clearLoginAttemptToken();
  },
};

export default function SpitialLogin() {
  var [isLoading, setLoading] = useState(false);
  var { phoneNumber, activeSection } = useSelector(function (state) { return state.Auth; });
  var [otp, setOTP] = React.useState(Array.from({ length: 5 }));
  var [profiles, setProfiles] = useState([]);
  var [sessions, setSessions] = useState([]);
  var [isDeletingSession, setIsDeletingSession] = useState(false);
  var [removingSessionId, setRemovingSessionId] = useState(null);
  var [publicAuthConfig, setPublicAuthConfig] = useState(function () {
    return getPublicAuthConfigSync();
  });
  var dispatch = useDispatch();
  var navigate = useNavigate();
  var location = useLocation();
  var [searchParams] = useSearchParams();
  var tokenGenerationInProgress = useRef(false);
  var loginMethodRef = useRef(null);
  var defaultPhoneFocusRef = useRef(false);
  var maxSessionCount = getMaxSessionCount(publicAuthConfig);
  var maxProfileCount = getMaxProfileCount(publicAuthConfig);

  useEffect(function () {
    var isMounted = true;

    fetchPublicAuthConfig().then(function (config) {
      if (isMounted) {
        setPublicAuthConfig(config);
      }
    });

    var unsubscribe = subscribePublicAuthConfig(function (payload) {
      if (isMounted) {
        setPublicAuthConfig(payload.config);
      }
    });

    return function () {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  /**
   * Generate Login Attempt Token
   */
  var generateLoginToken = useCallback(async function () {
    if (tokenGenerationInProgress.current) {
      return LoginAttemptStorage.getToken();
    }

    try {
      tokenGenerationInProgress.current = true;
      var data = await generateLoginAttemptToken(navigator.userAgent);
      var token = data && data.token;
      var expiresAt =
        (data && data.expiresAt) || getServerNow() + 15 * 60 * 1000;

      if (!token || typeof token !== "string") {
        return null;
      }

      LoginAttemptStorage.setToken(token, expiresAt);
      return token;
    } catch (error) {
      return null;
    } finally {
      tokenGenerationInProgress.current = false;
    }
  }, []);

  /**
   * Get a valid LAT token - generates if needed
   */
  var getValidToken = useCallback(async function () {
    var existingToken = LoginAttemptStorage.getToken();
    if (existingToken && LoginAttemptStorage.isTokenValid()) {
      return existingToken;
    }
    return await generateLoginToken();
  }, [generateLoginToken]);

  /**
   * Initialize LAT on mount and reset activeSection
   */
  useEffect(function () {
    dispatch(setActiveSeciton(0));
    if (!LoginAttemptStorage.isTokenValid()) {
      generateLoginToken();
    }
  }, [generateLoginToken, dispatch]);

  var handlePhoneNumber = function (e) {
    if (phoneNumber?.length === 11 && e !== "delete") {
      return;
    } else if (e === "delete") {
      dispatch(setPhone(phoneNumber?.slice(0, -1)));
    } else {
      dispatch(setPhone(phoneNumber?.toString() + e.toString()));
    }
  };

  /**
   * Request OTP - with automatic token regeneration on expiry
   */
  var requestOTPVerify = async function (isRetry) {
    try {
      if (!phoneNumber) return toast.error("شماره تلفن الزامی میباشد");
      if (phoneNumber.length !== 11)
        return toast.error("شماره تلفن وارد شده صحیح نیست");
      if (isLoading) return toast("لطفا صبر کنید");

      var regex = /^09\d{9}$/;
      if (!regex.test(phoneNumber)) {
        return toast.error("شماره تلفن وارد شده صحیح نیست");
      }

      setLoading(true);
      var token = await getValidToken();

      if (!token || typeof token !== 'string' || token === "undefined" || token === "null") {
        toast.error("خطا در ایجاد توکن امنیتی، لطفا صفحه را رفرش کنید");
        setLoading(false);
        return;
      }

      await sendLoginOTP({
        mobileNumber: Number(phoneNumber),
        loginAttemptToken: token,
      });

      toast.success("کد تایید برای شما ارسال شد");
      setLoading(false);
      dispatch(setActiveSeciton(1));
    } catch (error) {
      var errorMsg = getAuthErrorMessage(error);
      var statusCode = error.response?.status || error.response?.data?.statusCode;

      // Handle token expiry - auto regenerate and retry once
      if ((statusCode === 401 || errorMsg.toLowerCase().includes("expired") || errorMsg.includes("consumed")) && !isRetry) {
        LoginAttemptStorage.clear();
        // Generate new token and retry
        var newToken = await generateLoginToken();
        if (newToken) {
          setLoading(false);
          return requestOTPVerify(true);
        } else {
          toast.error("خطا در ایجاد توکن جدید، لطفا صفحه را رفرش کنید");
          setLoading(false);
          return;
        }
      }

      if (errorMsg.includes("User-Agent mismatch") || errorMsg.includes("token theft")) {
        LoginAttemptStorage.clear();
        toast.error("خطای امنیتی - لطفا دوباره تلاش کنید");
      } else {
        toast.error(errorMsg);
      }
      setLoading(false);
    }
  };

  var handleOTP = function (e) {
    var index = otp.findIndex(function (item) {
      return item === undefined || item === "";
    });
    if (index === -1 && e === "delete") index = 5;
    if (e === "delete") {
      var newOTP = [...otp];
      newOTP[index - 1] = "";
      setOTP(newOTP);
    } else if (e === "submit") {
      // submit action
    } else {
      var newOTP2 = [...otp];
      newOTP2[index] = e;
      setOTP(newOTP2);
    }
  };

  /**
   * Verify OTP - with automatic token regeneration on expiry
   */
  var verifyCode = async function (isRetry) {
    try {
      if (otp.filter(function (x) { return x === undefined || x === ""; }).length > 0)
        return toast.error("کد وارد شده صحیح نیست");
      if (isLoading) return toast("لطفا صبر کنید");

      setLoading(true);

      // Check local token validity first
      var token = LoginAttemptStorage.getToken();
      if (!token || LoginAttemptStorage.isTokenExpired()) {
        // Token expired locally - try to regenerate and retry
        if (!isRetry) {
          LoginAttemptStorage.clear();
          var newToken = await generateLoginToken();
          if (newToken) {
            setLoading(false);
            toast.info("توکن جدید ایجاد شد، لطفا دوباره کد را ارسال کنید");
            dispatch(setActiveSeciton(0));
            return;
          }
        }
        toast.error("نشست منقضی شده، لطفا دوباره تلاش کنید");
        setLoading(false);
        dispatch(setActiveSeciton(0));
        return;
      }

      var loginData = await loginRegister({
        mobileNumber: Number(phoneNumber),
        otp: Number(otp.join("")),
        loginAttemptToken: token,
      });

      loginMethodRef.current = "phone_otp";
      trackGoogleAnalyticsEvent("login_success", {
        login_method: "phone_otp",
      });

      // The profile-selection token can query active sessions before it is
      // consumed by select-profile. Preserve the historical ordering here:
      // login-register -> sessions -> user -> select-profile.
      var temporaryAccessToken = loginData && loginData.accessToken;
      var sessionsList = await getSessionsWithToken(temporaryAccessToken);

      if (sessionsList.length >= maxSessionCount) {
        setSessions(sessionsList);
        dispatch(setActiveSeciton(3));
        return;
      }

      var profileData = await getUser();
      setProfiles((profileData && profileData.profiles) || []);
      dispatch(setActiveSeciton(2));
    } catch (error) {
      var errorMsg = getAuthErrorMessage(error);
      var statusCode = error.response?.status || error.response?.data?.statusCode;

      // Handle token expiry from server - generate new token and go back to phone entry
      if ((statusCode === 401 || errorMsg.toLowerCase().includes("expired") || errorMsg.includes("consumed")) && !isRetry) {
        LoginAttemptStorage.clear();
        // Generate new token for next attempt
        await generateLoginToken();
        toast.error("توکن منقضی شده، لطفا دوباره شماره تلفن را وارد کنید");
        setOTP(Array.from({ length: 5 }));
        dispatch(setActiveSeciton(0));
        setLoading(false);
        return;
      }

      if (errorMsg.includes("User-Agent mismatch") || errorMsg.includes("token theft")) {
        LoginAttemptStorage.clear();
        toast.error("خطای امنیتی - لطفا دوباره تلاش کنید");
        dispatch(setActiveSeciton(0));
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Select Profile
   */
  var handleSelectProfile = async function (profileId) {
    try {
      if (isLoading) return toast("لطفا صبر کنید");
      setLoading(true);

      var qrTempToken = localStorage.getItem(
        AUTH_STORAGE_KEYS.QR_TEMP_TOKEN
      );
      var lat = LoginAttemptStorage.getToken();
      var data;

      if (qrTempToken) {
        data = await selectQRProfile(profileId, lat, qrTempToken);
      } else {
        data = await selectProfile({
          profileId: profileId,
          loginAttemptToken: lat || undefined,
        });
      }

      var accessToken = data && data.accessToken;

      if (!accessToken) {
        toast.error("خطا در دریافت توکن");
        setLoading(false);
        return;
      }

      localStorage.removeItem(AUTH_STORAGE_KEYS.QR_TEMP_TOKEN);
      LoginAttemptStorage.clear();

      trackGoogleAnalyticsEvent("profile_selected", {
        profile_id: profileId,
        login_method:
          loginMethodRef.current || (qrTempToken ? "qr_login" : "phone_otp"),
      });

      toast.success("به منه باخ خوش آمدید");
      dispatch(resetAuth());

      var redirect = searchParams.get("ref");
      if (redirect) {
        var redirectOptions = { replace: true };
        var entryPointGuide =
          NextPlayGuideHelper.getGuideFromNavigationState(location.state);
        if (entryPointGuide) {
          redirectOptions.state =
            NextPlayGuideHelper.withGuideNavigationState(null, entryPointGuide);
        }
        navigate(decodeURIComponent(redirect), redirectOptions);
      } else {
        navigate("/", { replace: true });
      }
    } catch (error) {
      var errorMsg = getAuthErrorMessage(error, "");
      var statusCode = error.response?.status || error.response?.data?.statusCode;

      if (
        statusCode === 409 ||
        errorMsg.includes("Maximum session limit") ||
        errorMsg.includes("حداکثر تعداد نشست")
      ) {
        await fetchSessions();
        dispatch(setActiveSeciton(3));
        toast.error("حداکثر تعداد نشست‌های مجاز رسیده است. لطفا یکی را حذف کنید.");
      } else {
        toast.error(errorMsg || "خطایی رخ داده است");
      }
      setLoading(false);
    }
  };

  /**
   * Fetch active sessions
   */
  var fetchSessions = async function () {
    try {
      var sessionToken =
        localStorage.getItem(AUTH_STORAGE_KEYS.QR_TEMP_TOKEN) ||
        getAccessToken();

      if (!sessionToken) {
        throw new Error("Profile-selection token is unavailable");
      }

      var sessionsList = await getSessionsWithToken(sessionToken);
      setSessions(sessionsList || []);
      return sessionsList || [];
    } catch (error) {
      toast.error("خطا در دریافت نشست‌های فعال");
      return null;
    }
  };

  /**
   * Delete a session through the current authenticated session contract.
   * QR login keeps its explicit temporary-token header because that token is
   * not stored in the standard auth slot until profile selection completes.
   */
  var handleDeleteSession = async function (sessionId) {
    try {
      if (isDeletingSession) return;
      setIsDeletingSession(true);
      setRemovingSessionId(sessionId);

      var sessionToken =
        localStorage.getItem(AUTH_STORAGE_KEYS.QR_TEMP_TOKEN) ||
        getAccessToken();

      if (!sessionToken) {
        throw new Error("توکن یافت نشد");
      }

      await deleteSessionWithToken(sessionToken, sessionId);
      toast.success("نشست با موفقیت حذف شد");

      var newSessions = sessions.filter(function (s) {
        return (s._id || s.sessionId) !== sessionId;
      });
      setSessions(newSessions);

      if (newSessions.length < maxSessionCount) {
        var profileData = await getUserWithToken(sessionToken);
        setProfiles((profileData && profileData.profiles) || []);
        dispatch(setActiveSeciton(2));
      }
    } catch (error) {
      var errorMsg = getAuthErrorMessage(error, "خطا در حذف نشست");
      toast.error(errorMsg);
    } finally {
      setIsDeletingSession(false);
      setRemovingSessionId(null);
    }
  };

  /**
   * Handle session modal close/cancel - return to phone input
   */
  var handleSessionModalClose = function () {
    dispatch(setActiveSeciton(0));
  };

  /**
   * Handle session removal request from modal
   */
  var handleRemoveSession = async function (sessionId) {
    await handleDeleteSession(sessionId);
  };

  var revertStep = function () {
    dispatch(setActiveSeciton(0));
  };

  /**
   * Handle QR Login Success
   */
  var handleQRLoginSuccess = async function (tokenData) {
    try {
      setLoading(true);
      loginMethodRef.current = "qr_login";
      if (tokenData?.temporaryToken || tokenData?.accessToken) {
        trackGoogleAnalyticsEvent("login_success", {
          login_method: "qr_login",
        });
      }

      if (tokenData.isTemporary && tokenData.temporaryToken) {
        localStorage.setItem(
          AUTH_STORAGE_KEYS.QR_TEMP_TOKEN,
          tokenData.temporaryToken
        );

        var qrSessions = await getSessionsWithToken(tokenData.temporaryToken);
        if (qrSessions.length >= maxSessionCount) {
          setSessions(qrSessions);
          dispatch(setActiveSeciton(3));
          return;
        }

        var data = await getUserWithToken(tokenData.temporaryToken);
        setProfiles((data && data.profiles) || []);
        toast.success("پروفایل خود را انتخاب کنید");
        dispatch(setActiveSeciton(2));
        return;
      }

      if (tokenData.accessToken) {
        setTemporaryAuthTokens({
          accessToken: tokenData.accessToken,
          refreshToken: tokenData.refreshToken,
        });

        var accessTokenSessions = await getSessionsWithToken(
          tokenData.accessToken
        );
        if (accessTokenSessions.length >= maxSessionCount) {
          setSessions(accessTokenSessions);
          dispatch(setActiveSeciton(3));
          return;
        }

        var profData = await getUserWithToken(tokenData.accessToken);
        setProfiles((profData && profData.profiles) || []);
        toast.success("ورود موفق");
        dispatch(setActiveSeciton(2));
        return;
      }

      toast.error("خطا در دریافت اطلاعات ورود");
    } catch (error) {
      toast.error(getAuthErrorMessage(error, "خطا"));
    } finally {
      setLoading(false);
    }
  };

  // Define steps with lazy-loaded components wrapped in Suspense
  var steps = [
    {
      id: "login-view",
      children: (
        <Suspense fallback={<LoadingSpinner />}>
          <LoginPageContent
            isLoading={isLoading}
            handlePhoneNumber={handlePhoneNumber}
            requestOTPVerify={requestOTPVerify}
            handleQRLoginSuccess={handleQRLoginSuccess}
            activeSection={activeSection}
          />
        </Suspense>
      ),
    },
    {
      id: "otp-view",
      children: (
        <Suspense fallback={<LoadingSpinner />}>
          <LoginVerifyPageContent
            isLoading={isLoading}
            handleOTP={handleOTP}
            otp={otp}
            verifyCode={verifyCode}
            revertStep={revertStep}
          />
        </Suspense>
      ),
    },
    {
      id: "select-profile-view",
      children: (
        <Suspense fallback={<LoadingSpinner />}>
          <SelectProfileView
            isLoading={isLoading}
            handleSelectProfile={handleSelectProfile}
            profiles={profiles}
            maxProfileCount={maxProfileCount}
          />
        </Suspense>
      ),
    },
    {
      id: "session-management-view",
      children: (
        <Suspense fallback={<LoadingSpinner />}>
          <SessionManagementModal
            open={true}
            sessions={sessions}
            onRemoveSession={handleRemoveSession}
            onCancel={handleSessionModalClose}
            isRemoving={isDeletingSession}
            removingSessionId={removingSessionId}
            maxSessionCount={maxSessionCount}
          />
        </Suspense>
      ),
    },
  ];

  // Reset OTP when navigating back to login step
  useEffect(function () {
    var timers = [];

    if (activeSection === 0) {
      setOTP(Array.from({ length: 5 }));
      defaultPhoneFocusRef.current = false;
      timers = [0, 100, 300, 700, 1200, 2000].map(function (delay) {
        return setTimeout(function () {
          focusPhoneLoginDefaultOnce(defaultPhoneFocusRef);
        }, delay);
      });
    }

    return function () {
      timers.forEach(function (timer) {
        clearTimeout(timer);
      });
    };
  }, [activeSection]);

  return (
    <div className="legacy-login-app">
      <div className="legacy-login-sidebar-cell">
        <Menu focusKey="MENU" />
      </div>
      <div className="legacy-login-content-cell">
        <div className="legacy-login-main-card">
          <SlideAnimationFrame
            activeSectionIndex={activeSection}
            sections={steps}
          />
        </div>
      </div>

    </div>
  );
}
